import React, { useState } from 'react';
import { ExternalLink, ArrowRight, Phone } from 'lucide-react';
import { projects } from '../data/mock';
import { Link } from 'react-router-dom';

const Realisations = () => {
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  
  const categories = ['Tous', ...new Set(projects.map(p => p.category))];
  
  const filteredProjects = selectedCategory === 'Tous' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Page Header */}
      <section className="text-white py-20 mb-16" style={{
        background: 'linear-gradient(135deg, #0A2463 0%, #0D2F7E 50%, #1035A0 100%)'
      }}>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
          }}>
            Nos <span style={{ 
              background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Réalisations</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Découvrez quelques-uns de nos projets récents. Chaque réalisation témoigne de notre savoir-faire et de notre engagement envers l'excellence.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="container mx-auto px-4 mb-12">
        <div className="flex flex-wrap justify-center gap-3">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2.5 rounded-full font-medium transition-all ${
                selectedCategory === category
                  ? 'bg-brand-gold text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Projects Grid */}
      <section className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div 
              key={project.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="relative overflow-hidden aspect-[4/3]">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <button className="flex items-center gap-2 text-white font-medium">
                    <ExternalLink size={20} />
                    Voir le projet
                  </button>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-brand-gold text-white px-3 py-1 rounded-full text-sm font-medium">
                    {project.year}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <span className="text-brand-blue text-sm font-semibold uppercase tracking-wide">
                  {project.category}
                </span>
                <h3 className="text-xl font-bold text-gray-800 mt-2 mb-3 group-hover:text-brand-gold transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {project.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="container mx-auto px-4 mt-20">
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8 md:p-12" style={{
          border: '4px solid transparent',
          backgroundImage: 'linear-gradient(to bottom right, rgb(249, 250, 251), white), linear-gradient(to right, #f9a01b, #ffd230)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box'
        }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-brand-gold mb-2">15+</div>
              <div className="text-gray-600 font-medium">Années d'Expérience</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-brand-gold mb-2">30+</div>
              <div className="text-gray-600 font-medium">Projets Réalisés</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-brand-gold mb-2">100%</div>
              <div className="text-gray-600 font-medium">Clients Satisfaits</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 mt-16">
        <div className="rounded-2xl shadow-2xl p-8 md:p-12 text-center" style={{
          background: 'linear-gradient(135deg, #0A2463 0%, #0D2F7E 50%, #1035A0 100%)'
        }}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
            Votre Projet <span style={{ 
              background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>en Images</span>
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto mt-6">
            Chaque réalisation commence par votre vision. Parlons-en ensemble et créons quelque chose d'exceptionnel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-brand-gold text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-gold-dark transition-all hover:shadow-xl hover:scale-105"
            >
              Demander mon devis gratuit
              <ArrowRight size={20} />
            </Link>
            <a 
              href="tel:0471664418"
              className="inline-flex items-center justify-center gap-2 bg-white text-brand-hero px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brand-gold hover:text-gray-900 transition-all hover:shadow-xl"
            >
              <Phone size={20} />
              Appelez-nous
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Realisations;
